import {
  defaultSpaceOperatingHours,
  spaceAccessibilityFeatureIds,
  spaceAmenityIds,
  spaceStatuses,
  spaceTypeIds,
  spaceVisibilities,
  type OrganizationSpaceStatus,
  type OrganizationSpaceVisibility,
  type SpaceAccessibility,
  type SpaceAccessibilityFeatureId,
  type SpaceAmenityId,
  type SpaceMediaReference,
  type SpaceOperatingHours,
  type SpaceTypeId,
} from "../../../packages/foundation/src/index.js";

export type SpaceEditorInput = {
  spaceId: string;
  campusId: string;
  type: string;
  status: string;
  visibility: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  locationName: string;
  capacity: string;
  amenities: string;
  accessibilityFeatures: string;
  accessibilityNotes: string;
  accessibilityNotesAr: string;
  primaryImageStoragePath: string;
  primaryImageAltText: string;
  primaryImageAltTextAr: string;
  timezone: string;
  mondayHours: string;
  tuesdayHours: string;
  wednesdayHours: string;
  thursdayHours: string;
  fridayHours: string;
  saturdayHours: string;
  sundayHours: string;
  hourlyPriceUsd: string;
};

export type SpaceEditorDraft = {
  spaceId: string;
  campusId: string | null;
  type: SpaceTypeId;
  status: OrganizationSpaceStatus;
  visibility: OrganizationSpaceVisibility;
  name: string;
  nameAr: string | null;
  description: string;
  descriptionAr: string | null;
  locationName: string;
  capacity: number;
  amenities: SpaceAmenityId[];
  accessibility: SpaceAccessibility;
  primaryImage: SpaceMediaReference | null;
  galleryImages: SpaceMediaReference[];
  timezone: string;
  operatingHours: SpaceOperatingHours;
  hourlyPriceCents: number | null;
  currency: "usd" | null;
  source: "experience_thirty_nine";
};

export type SpaceEditorResult =
  | {
      ok: true;
      draft: SpaceEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type SpaceEditorFixture = {
  accessibility?: SpaceAccessibility;
  amenities?: SpaceAmenityId[];
  campusId?: string | null;
  capacity: number;
  description: string;
  descriptionAr: string | null;
  galleryImages?: SpaceMediaReference[];
  hourlyPriceCents: number | null;
  id: string;
  locationName: string;
  name: string;
  nameAr: string | null;
  operatingHours?: SpaceOperatingHours;
  primaryImage?: SpaceMediaReference | null;
  status?: OrganizationSpaceStatus;
  timezone?: string;
  type?: SpaceTypeId;
  visibility?: OrganizationSpaceVisibility;
};

export const spaceEditorTypeOptions = spaceTypeIds;
export const spaceEditorStatusOptions = spaceStatuses;
export const spaceEditorVisibilityOptions = spaceVisibilities;
export const spaceEditorAmenityOptions = spaceAmenityIds;
export const spaceEditorAccessibilityOptions = spaceAccessibilityFeatureIds;

const idLimit = 80;
const nameLimit = 120;
const descriptionLimit = 800;
const locationLimit = 160;
const timezoneLimit = 80;
const maxHourlyPriceCents = 100000;
const timeIntervalPattern = /^(([01]\d|2[0-3]):[0-5]\d)-(([01]\d|2[0-3]):[0-5]\d)$/;

export function buildSpaceEditorInputFromFixture(fixture: SpaceEditorFixture): SpaceEditorInput {
  const operatingHours = fixture.operatingHours ?? defaultSpaceOperatingHours();
  const accessibility = fixture.accessibility ?? {
    features: [],
    notes: null,
  };

  return {
    accessibilityFeatures: accessibility.features.join(","),
    accessibilityNotes: accessibility.notes?.en ?? "",
    accessibilityNotesAr: accessibility.notes?.ar ?? "",
    amenities: (fixture.amenities ?? []).join(","),
    campusId: fixture.campusId ?? "",
    capacity: String(fixture.capacity),
    description: fixture.description,
    descriptionAr: fixture.descriptionAr ?? "",
    fridayHours: operatingHours.friday,
    hourlyPriceUsd:
      fixture.hourlyPriceCents === null ? "" : formatUsdInputValue(fixture.hourlyPriceCents),
    locationName: fixture.locationName,
    mondayHours: operatingHours.monday,
    name: fixture.name,
    nameAr: fixture.nameAr ?? "",
    primaryImageAltText: fixture.primaryImage?.altText.en ?? "",
    primaryImageAltTextAr: fixture.primaryImage?.altText.ar ?? "",
    primaryImageStoragePath: fixture.primaryImage?.storagePath ?? "",
    saturdayHours: operatingHours.saturday,
    spaceId: fixture.id,
    status: fixture.status ?? "active",
    sundayHours: operatingHours.sunday,
    thursdayHours: operatingHours.thursday,
    timezone: fixture.timezone ?? "America/New_York",
    tuesdayHours: operatingHours.tuesday,
    type: fixture.type ?? "multipurposeRoom",
    visibility: fixture.visibility ?? "connected",
    wednesdayHours: operatingHours.wednesday,
  };
}

export function buildDefaultSpaceEditorInput(): SpaceEditorInput {
  return buildSpaceEditorInputFromFixture({
    accessibility: {
      features: ["wheelchairAccessible", "accessibleEntrance"],
      notes: {
        ar: "مدخل مهيأ للمجتمع.",
        en: "Accessible community entrance.",
      },
    },
    amenities: ["tables", "chairs", "wifi"],
    campusId: "",
    capacity: 80,
    description: "Synthetic space for Shdeed development testing.",
    descriptionAr: "مساحة تجريبية لاختبار تطوير شديد.",
    hourlyPriceCents: null,
    id: "community-hall",
    locationName: "Community building",
    name: "Community hall",
    nameAr: "قاعة المجتمع",
    operatingHours: defaultSpaceOperatingHours(),
    primaryImage: null,
    status: "active",
    timezone: "America/New_York",
    type: "multipurposeRoom",
    visibility: "connected",
  });
}

export function buildEmptySpaceEditorInput(): SpaceEditorInput {
  return {
    accessibilityFeatures: "",
    accessibilityNotes: "",
    accessibilityNotesAr: "",
    amenities: "",
    campusId: "",
    capacity: "",
    description: "",
    descriptionAr: "",
    fridayHours: "",
    hourlyPriceUsd: "",
    locationName: "",
    mondayHours: "",
    name: "",
    nameAr: "",
    primaryImageAltText: "",
    primaryImageAltTextAr: "",
    primaryImageStoragePath: "",
    saturdayHours: "",
    spaceId: "",
    status: "",
    sundayHours: "",
    thursdayHours: "",
    timezone: "",
    tuesdayHours: "",
    type: "",
    visibility: "",
    wednesdayHours: "",
  };
}

export function evaluateSpaceEditorInput(input: SpaceEditorInput): SpaceEditorResult {
  const spaceId = normalizeSpaceDocumentId(input.spaceId);
  const campusId = input.campusId.trim();
  const type = input.type.trim();
  const status = input.status.trim();
  const visibility = input.visibility.trim();
  const name = input.name.trim();
  const nameAr = emptyToNull(input.nameAr);
  const description = input.description.trim();
  const descriptionAr = emptyToNull(input.descriptionAr);
  const locationName = input.locationName.trim();
  const capacity = parseWholeNumber(input.capacity);
  const amenities = parseIdList(input.amenities);
  const accessibilityFeatures = parseIdList(input.accessibilityFeatures);
  const accessibilityNotes = emptyToNull(input.accessibilityNotes);
  const accessibilityNotesAr = emptyToNull(input.accessibilityNotesAr);
  const primaryImageStoragePath = input.primaryImageStoragePath.trim();
  const primaryImageAltText = input.primaryImageAltText.trim();
  const primaryImageAltTextAr = emptyToNull(input.primaryImageAltTextAr);
  const timezone = input.timezone.trim();
  const operatingHours = {
    friday: input.fridayHours.trim(),
    monday: input.mondayHours.trim(),
    saturday: input.saturdayHours.trim(),
    sunday: input.sundayHours.trim(),
    thursday: input.thursdayHours.trim(),
    tuesday: input.tuesdayHours.trim(),
    wednesday: input.wednesdayHours.trim(),
  };
  const hourlyPriceCents = parseOptionalUsdInputToCents(input.hourlyPriceUsd);
  const errors: string[] = [];

  validateRequiredText("Space ID", spaceId, idLimit, errors);
  validateOptionalText("Campus ID", campusId, idLimit, errors);
  validateRequiredText("English name", name, nameLimit, errors);
  validateOptionalText("Arabic name", nameAr, nameLimit, errors);
  validateRequiredText("English description", description, descriptionLimit, errors);
  validateOptionalText("Arabic description", descriptionAr, descriptionLimit, errors);
  validateRequiredText("Location", locationName, locationLimit, errors);
  validateRequiredText("Timezone", timezone, timezoneLimit, errors);

  if (!spaceTypeIds.includes(type as SpaceTypeId)) {
    errors.push("Space type must be one of the canonical catalog types.");
  }

  if (!spaceStatuses.includes(status as OrganizationSpaceStatus)) {
    errors.push("Space status must be draft, active, unavailable, or archived.");
  }

  if (!spaceVisibilities.includes(visibility as OrganizationSpaceVisibility)) {
    errors.push("Space visibility must be private, connected, or public.");
  }

  if (capacity === undefined) {
    errors.push("Capacity must be a whole number from 0 to 5,000.");
  }

  if (amenities.some((amenity) => !spaceAmenityIds.includes(amenity as SpaceAmenityId))) {
    errors.push("Amenities must use canonical identifiers.");
  }

  if (
    accessibilityFeatures.some(
      (feature) => !spaceAccessibilityFeatureIds.includes(feature as SpaceAccessibilityFeatureId),
    )
  ) {
    errors.push("Accessibility features must use canonical identifiers.");
  }

  for (const [day, value] of Object.entries(operatingHours)) {
    if (!isValidOperatingHoursValue(value)) {
      errors.push(`${day} hours must be closed or compact HH:mm-HH:mm intervals.`);
    }
  }

  if (hourlyPriceCents === undefined) {
    errors.push("Legacy hourly price must be blank or a valid USD amount.");
  } else if (hourlyPriceCents !== null && hourlyPriceCents > maxHourlyPriceCents) {
    errors.push("Legacy hourly price must be at most $1,000.");
  }

  if (primaryImageStoragePath) {
    if (
      !primaryImageStoragePath.match(
        /^organizations\/[a-z0-9_-]+\/spaces\/[a-z0-9][a-z0-9_-]{0,79}\/[A-Za-z0-9][A-Za-z0-9._/-]{0,240}$/,
      )
    ) {
      errors.push("Primary image path must be scoped under an organization space folder.");
    }
    validateRequiredText("Primary image English alt text", primaryImageAltText, 160, errors);
    validateOptionalText("Primary image Arabic alt text", primaryImageAltTextAr, 160, errors);
  } else if (primaryImageAltText || primaryImageAltTextAr) {
    errors.push("Primary image path is required when alt text is provided.");
  }

  if (
    errors.length > 0 ||
    capacity === undefined ||
    hourlyPriceCents === undefined ||
    !spaceTypeIds.includes(type as SpaceTypeId) ||
    !spaceStatuses.includes(status as OrganizationSpaceStatus) ||
    !spaceVisibilities.includes(visibility as OrganizationSpaceVisibility)
  ) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      accessibility: {
        features: uniqueIds(accessibilityFeatures) as SpaceAccessibilityFeatureId[],
        notes:
          accessibilityNotes || accessibilityNotesAr
            ? {
                ar: accessibilityNotesAr,
                en: accessibilityNotes ?? "",
              }
            : null,
      },
      amenities: uniqueIds(amenities) as SpaceAmenityId[],
      campusId: campusId || null,
      capacity,
      currency: hourlyPriceCents === null ? null : "usd",
      description,
      descriptionAr,
      galleryImages: [],
      hourlyPriceCents,
      locationName,
      name,
      nameAr,
      operatingHours,
      primaryImage: primaryImageStoragePath
        ? {
            altText: {
              ar: primaryImageAltTextAr,
              en: primaryImageAltText,
            },
            sortOrder: 0,
            storagePath: primaryImageStoragePath,
            visibility: "inherit",
          }
        : null,
      source: "experience_thirty_nine",
      spaceId,
      status: status as OrganizationSpaceStatus,
      timezone,
      type: type as SpaceTypeId,
      visibility: visibility as OrganizationSpaceVisibility,
    },
  };
}

export function normalizeSpaceDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, idLimit);
}

function parseWholeNumber(value: string): number | undefined {
  const trimmed = value.trim();

  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }

  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 5000 ? parsed : undefined;
}

function parseIdList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function uniqueIds(values: string[]): string[] {
  return [...new Set(values)];
}

function formatUsdInputValue(amountCents: number): string {
  if (amountCents % 100 === 0) {
    return String(amountCents / 100);
  }

  return (amountCents / 100).toFixed(2);
}

function parseOptionalUsdInputToCents(value: string): number | null | undefined {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return undefined;
  }

  const parts = trimmed.split(".");
  const dollarsPart = parts[0];
  const centsPart = parts[1] ?? "";

  if (!dollarsPart) {
    return undefined;
  }

  const dollars = Number(dollarsPart);

  if (!Number.isSafeInteger(dollars)) {
    return undefined;
  }

  const cents = Number(centsPart.padEnd(2, "0"));
  const totalCents = dollars * 100 + cents;
  return Number.isSafeInteger(totalCents) ? totalCents : undefined;
}

function isValidOperatingHoursValue(value: string): boolean {
  if (value === "closed") {
    return true;
  }

  const intervals = value.split(",");
  if (intervals.length < 1 || intervals.length > 3) {
    return false;
  }

  return intervals.every((interval) => {
    const match = interval.match(timeIntervalPattern);
    if (!match) {
      return false;
    }
    const startsAt = match[1];
    const endsAt = match[3];
    return (
      startsAt !== undefined &&
      endsAt !== undefined &&
      minutesFromTime(startsAt) < minutesFromTime(endsAt)
    );
  });
}

function minutesFromTime(value: string): number {
  const [hour = 0, minute = 0] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: string | null,
  maxLength: number,
  errors: string[],
): void {
  if (value !== null && value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
